import React, { useState } from 'react';
import { BilingualText } from '../components/BilingualText';
import { ArrowLeft, FileBarChart, Package, Calendar, Warehouse, Users, Truck, Download, Filter, Search, ChevronRight, Printer, FileText, Table as TableIcon } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
type ReportCategory = 'Shipments' | 'Appointments' | 'Performance' | 'Capacity' | 'Suppliers' | 'Receiving';
interface ReportDefinition {
  id: string;
  title: string;
  category: ReportCategory;
  description: string;
}
const reports: ReportDefinition[] = [{
  id: 'shp-sum',
  title: 'Shipment Summary Report',
  category: 'Shipments',
  description: 'Overview of all shipments with status and quantities.'
}, {
  id: 'shp-stat',
  title: 'Shipment Status Distribution',
  category: 'Shipments',
  description: 'Breakdown of shipments by their current lifecycle stage.'
}, {
  id: 'apt-sum',
  title: 'Appointment Summary',
  category: 'Appointments',
  description: 'Detailed list of all reserved slots and their outcomes.'
}, {
  id: 'apt-del',
  title: 'Appointment Delay Report',
  category: 'Appointments',
  description: 'Analysis of late arrivals and waiting times.'
}, {
  id: 'wh-perf',
  title: 'Receiving Performance',
  category: 'Performance',
  description: 'Warehouse efficiency metrics and daily throughput.'
}, {
  id: 'cap-util',
  title: 'Capacity Utilization',
  category: 'Capacity',
  description: 'Daily, weekly, and monthly warehouse loading analysis.'
}, {
  id: 'sup-score',
  title: 'Supplier Scorecard',
  category: 'Suppliers',
  description: 'Reliability metrics: On-time %, No-shows, and Cancellations.'
}, {
  id: 'rec-dur',
  title: 'Receiving Duration Report',
  category: 'Receiving',
  description: 'Time analysis from check-in to completion.'
}];
export function ReportCenter() {
  const [selectedCategory, setSelectedCategory] = useState<ReportCategory | 'All'>('All');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const filteredReports = reports.filter(r => (selectedCategory === 'All' || r.category === selectedCategory) && (r.title.toLowerCase().includes(searchTerm.toLowerCase()) || r.description.toLowerCase().includes(searchTerm.toLowerCase())));
  return <div className="min-h-screen bg-slate-50 pb-24" data-id="element-854">
      <header className="bg-white px-4 py-6 border-b border-slate-200 flex items-center justify-between sticky top-0 z-10 lg:px-8" data-id="element-855">
        <div className="flex items-center gap-4" data-id="element-856">
          <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-857">
            <ArrowLeft size={24} data-id="element-858" />
          </button>
          <BilingualText en="Report Center" ar="مركز التقارير" className="text-xl font-bold" data-id="element-859" />
        </div>
        <div className="flex gap-2" data-id="element-860">
          <button className="btn-secondary py-2 px-4 text-sm hidden sm:flex" data-id="element-861">
            <History size={18} data-id="element-862" />
            <span data-id="element-863">Export History</span>
          </button>
        </div>
      </header>

      <main className="p-4 md:p-8 max-w-7xl mx-auto space-y-8" data-id="element-864">
        {/* Search and Category Filters */}
        <div className="space-y-6" data-id="element-865">
          <div className="relative max-w-2xl" data-id="element-866">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} data-id="element-867" />
            <input type="text" placeholder="Search for a report..." className="w-full pl-11 pr-4 py-4 bg-white border border-slate-200 rounded-2xl shadow-sm focus:ring-2 focus:ring-[#2E3192] outline-none transition-all text-lg" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-868" />
          </div>

          <div className="flex flex-wrap gap-2" data-id="element-869">
            <CategoryChip label="All" active={selectedCategory === 'All'} onClick={() => setSelectedCategory('All')} data-id="element-870" />
            <CategoryChip label="Shipments" active={selectedCategory === 'Shipments'} onClick={() => setSelectedCategory('Shipments')} icon={Package} data-id="element-871" />
            <CategoryChip label="Appointments" active={selectedCategory === 'Appointments'} onClick={() => setSelectedCategory('Appointments')} icon={Calendar} data-id="element-872" />
            <CategoryChip label="Performance" active={selectedCategory === 'Performance'} onClick={() => setSelectedCategory('Performance')} icon={TrendingUp} data-id="element-873" />
            <CategoryChip label="Capacity" active={selectedCategory === 'Capacity'} onClick={() => setSelectedCategory('Capacity')} icon={Warehouse} data-id="element-874" />
            <CategoryChip label="Suppliers" active={selectedCategory === 'Suppliers'} onClick={() => setSelectedCategory('Suppliers')} icon={Users} data-id="element-875" />
          </div>
        </div>

        {/* Reports Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-876">
          {filteredReports.map(report => <Link to={`/reports/${report.id}`} key={report.id} className="card p-6 hover:border-[#2E3192] hover:shadow-md transition-all group" data-id="element-877">
              <div className="flex justify-between items-start mb-4" data-id="element-878">
                <div className={`p-3 rounded-xl bg-slate-50 text-slate-400 group-hover:bg-blue-50 group-hover:text-[#2E3192] transition-colors`} data-id="element-879">
                  <FileBarChart size={24} data-id="element-880" />
                </div>
                <ChevronRight className="text-slate-300 group-hover:text-[#2E3192] transition-all transform group-hover:translate-x-1" size={20} data-id="element-881" />
              </div>
              <div className="space-y-2" data-id="element-882">
                <div className="text-[10px] font-bold text-[#2E3192] uppercase tracking-widest" data-id="element-883">
                  {report.category}
                </div>
                <h3 className="font-bold text-slate-800 text-lg leading-tight" data-id="element-884">
                  {report.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed" data-id="element-885">
                  {report.description}
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-slate-50 flex items-center gap-4 text-xs font-bold text-slate-400" data-id="element-886">
                <span className="flex items-center gap-1" data-id="element-887">
                  <Download size={14} data-id="element-888" /> PDF/Excel
                </span>
                <span className="flex items-center gap-1" data-id="element-889">
                  <Printer size={14} data-id="element-890" /> Print
                </span>
              </div>
            </Link>)}
        </div>

        {filteredReports.length === 0 && <div className="text-center py-20 space-y-4" data-id="element-891">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto text-slate-300" data-id="element-892">
              <Search size={40} data-id="element-893" />
            </div>
            <div className="space-y-1" data-id="element-894">
              <h3 className="font-bold text-slate-800" data-id="element-895">No reports found</h3>
              <p className="text-slate-500" data-id="element-896">
                Try adjusting your search or category filters.
              </p>
            </div>
          </div>}
      </main>
    </div>;
}
function CategoryChip({
  label,
  active,
  onClick,
  icon: Icon
}: any) {
  return <button onClick={onClick} className={`px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2 transition-all ${active ? 'bg-[#2E3192] text-white shadow-lg shadow-blue-900/20' : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'}`} data-id="element-897">
      {Icon && <Icon size={16} data-id="element-898" />}
      {label}
    </button>;
}
function TrendingUp({
  size
}: {
  size: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-899">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" data-id="element-900"></polyline>
      <polyline points="17 6 23 6 23 12" data-id="element-901"></polyline>
    </svg>;
}
function History({
  size
}: {
  size: number;
}) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-902">
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" data-id="element-903"></path>
      <path d="M3 3v5h5" data-id="element-904"></path>
      <path d="M12 7v5l4 2" data-id="element-905"></path>
    </svg>;
}